import type { Permission } from '../api/schema'

/**
 * Which `read` permission each `$org` section needs.
 *
 * One table, two consumers: the route guard mounted in `routes/$org.tsx` (which
 * redirects to `/$org` when the section is out of reach) and `AppSidebar`
 * (which hides the entry). Keeping them on the same table is what guarantees
 * the sidebar never links somewhere the guard will bounce.
 *
 * Keys are path segments *under* `/$org`, matched longest-prefix-first, so
 * `settings/members` can require more than `settings`. A section absent from
 * the table is ungated: the home page, the search page (results are filtered
 * per resource server-side), the user's own profile, the `patterns` and
 * `design-reference` developer references and the login / password-reset entry
 * points.
 */
export const SECTION_PERMISSIONS: Record<string, Permission> = {
    // Assets and everything hanging off the asset graph.
    assets: 'assets.read',
    createBuilding: 'assets.read',
    createBuildingGroup: 'assets.read',
    graph: 'assets.read',
    labels: 'assets.read',
    'plan-rendering': 'assets.read',

    documents: 'documents.read',
    interventions: 'interventions.read',
    issues: 'issues.read',
    steering: 'issues.read',
    group: 'group_steering.read',
    'data-protection': 'data_protection.read',
    signatures: 'signatures.read',
    knowledge: 'knowledge.read',
    campaigns: 'campaigns.read',
    benchmark: 'finance.read',
    contracts: 'contracts.read',
    leases: 'contracts.read',
    listings: 'listings.read',
    'rental-applications': 'rental_applications.read',
    expenses: 'finance.read',
    'capex-plans': 'capex.read',

    // The compliance catalogue is part of the compliance resource, not of the
    // generic configuration one (spec catalogue table).
    'compliance-controls': 'compliance.read',
    'compliance-rules': 'compliance.read',
    // The rules page reads and writes the compliance rules, so it carries the
    // permissions the server enforces on them (spec 2026-10-27-21 § 3).
    'business-rules': 'compliance.read',
    // Remediation scenarios are read with the rulebook (spec 2026-10-28-15 § 10).
    'remediation-scenarios': 'compliance.read',

    // Parties live under /users in the URL but under `contacts` in the
    // catalogue; org members are a different resource entirely (see below).
    users: 'contacts.read',

    // The type system / ontology.
    aggregations: 'configuration.read',
    'asset-types': 'configuration.read',
    'contract-stakeholder-types': 'configuration.read',
    'contract-types': 'configuration.read',
    derivations: 'configuration.read',
    'document-types': 'configuration.read',
    'folder-layouts': 'configuration.read',
    normalizations: 'configuration.read',
    properties: 'configuration.read',
    'property-mapping-rules': 'configuration.read',
    'relation-types': 'configuration.read',
    tags: 'configuration.read',
    types: 'configuration.read',

    // Jobs, imports and the data console are the `data` resource.
    data: 'data.read',
    imports: 'data.read',
    // A run page shows the internals: data.write, or a platform admin
    // (PLATFORM_ADMIN_SECTIONS below, spec 2026-10-21-19 §4).
    'agent-runs': 'data.write',
    jobs: 'data.read',

    // The organization itself.
    activity: 'organization.read',
    settings: 'organization.read',
    'settings/ai-models': 'organization.read',
    'settings/archive': 'archive.read',
    'settings/billing': 'billing.read',
    'settings/feature-flags': 'organization.read',
    'settings/organization': 'organization.read',
    'settings/tags': 'configuration.read',
    'settings/members': 'members.read',
    'settings/roles': 'members.read',
    'settings/webhooks': 'webhooks.read',
}

/**
 * Sections a platform admin opens whatever the role of their membership: the
 * agent run page (spec 2026-10-21-19 §4), whose internals routes let them
 * through on the backend too.
 */
export const PLATFORM_ADMIN_SECTIONS: readonly string[] = ['agent-runs']

/** isPlatformAdminSection: whether the path is under a PLATFORM_ADMIN_SECTIONS entry. */
export function isPlatformAdminSection(pathname: string, org: string): boolean {
    const prefix = `/${org}/`
    if (!pathname.startsWith(prefix)) return false
    const rest = pathname.slice(prefix.length)

    return PLATFORM_ADMIN_SECTIONS.some((key) => rest === key || rest.startsWith(`${key}/`))
}

const SECTION_KEYS = Object.keys(SECTION_PERMISSIONS).sort(
    (a, b) => b.length - a.length
)

/**
 * requiredPermissionForPath maps a full pathname (`/demo/settings/members`) to
 * the permission it needs, or `undefined` when the section is ungated.
 */
export function requiredPermissionForPath(
    pathname: string,
    org: string
): Permission | undefined {
    const prefix = `/${org}`
    if (!pathname.startsWith(prefix)) return undefined
    const rest = pathname.slice(prefix.length).replace(/^\/+/, '')
    if (rest === '') return undefined
    for (const key of SECTION_KEYS) {
        if (rest === key || rest.startsWith(`${key}/`)) {
            return SECTION_PERMISSIONS[key]
        }
    }
    return undefined
}
